import React, { useState, useEffect, useRef } from 'react';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import {
  getRecordedAudio,
  saveRecordedAudio,
  deleteRecordedAudio,
} from '../utils/audioStorage';
import { playSound } from '../audio';

interface AudioRecorderSectionProps {
  word: string;
  onAudioChange?: () => void;
}

export const AudioRecorderSection: React.FC<AudioRecorderSectionProps> = ({
  word,
  onAudioChange,
}) => {
  const normalizedWord = word.trim().toLowerCase();
  const [existingAudio, setExistingAudio] = useState<string | null>(null);
  const [isPlayingExisting, setIsPlayingExisting] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    isRecording,
    recordingTime,
    audioDataUrl,
    error,
    prepareMicrophone,
    startRecording,
    stopRecording,
    resetRecording,
    playPreview,
    isPlayingPreview,
  } = useVoiceRecorder();

  // 2-second countdown before recording begins
  const triggerRecordingWithCountdown = async () => {
    playSound('click');
    resetRecording();
    try {
      // 1. Request microphone access immediately on user click to maintain valid user activation
      await prepareMicrophone();
      // 2. Start 2-second countdown
      setCountdown(2);
    } catch (err: any) {
      console.warn('Microphone permission error on trigger:', err);
    }
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(null);
    resetRecording();
    playSound('click');
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      playSound('click');
      countdownTimerRef.current = setTimeout(() => {
        setCountdown((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
      return () => {
        if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      };
    } else if (countdown === 0) {
      playSound('stele');
      setCountdown(null);
      startRecording();
    }
  }, [countdown, startRecording]);

  // Load existing audio on mount or word change and listen for background storage hydration
  useEffect(() => {
    const updateAudio = () => {
      if (normalizedWord) {
        setExistingAudio(getRecordedAudio(normalizedWord));
      } else {
        setExistingAudio(null);
      }
    };

    updateAudio();
    resetRecording();
    setSaveSuccessMessage(null);

    const handleAudioUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ word?: string; all?: boolean }>;
      if (customEvent.detail?.all || customEvent.detail?.word === normalizedWord) {
        updateAudio();
      }
    };

    window.addEventListener('kabyle_audio_updated', handleAudioUpdated);
    return () => {
      window.removeEventListener('kabyle_audio_updated', handleAudioUpdated);
    };
  }, [normalizedWord, resetRecording]);

  const handlePlayExisting = () => {
    if (!existingAudio) return;
    setIsPlayingExisting(true);
    const audio = new Audio(existingAudio);
    audio.onended = () => setIsPlayingExisting(false);
    audio.onerror = () => setIsPlayingExisting(false);
    audio.play().catch(() => setIsPlayingExisting(false));
  };

  const handleSaveTake = async () => {
    if (!audioDataUrl || !normalizedWord) return;
    setExistingAudio(audioDataUrl);
    await saveRecordedAudio(normalizedWord, audioDataUrl);
    resetRecording();
    setSaveSuccessMessage("Audio sauvegardé (IndexedDB + Serveur) !");
    setTimeout(() => setSaveSuccessMessage(null), 3000);
    playSound('correct');
    if (onAudioChange) onAudioChange();
  };

  const handleDeleteExisting = async () => {
    if (!normalizedWord) return;
    await deleteRecordedAudio(normalizedWord);
    setExistingAudio(null);
    resetRecording();
    playSound('click');
    if (onAudioChange) onAudioChange();
  };

  return (
    <div className="bg-white/90 p-3.5 rounded-xl border border-amber-900/15 shadow-2xs space-y-2.5">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold uppercase tracking-wider text-amber-950 flex items-center gap-1.5">
          <span>🎙️ Enregistrement Vocal Studio</span>
          <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded font-bold">
            Mode Admin
          </span>
        </label>

        {existingAudio && !isRecording && !audioDataUrl && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Enregistré</span>
          </span>
        )}
      </div>

      {error && (
        <div className="p-2 rounded-lg bg-rose-50 border border-rose-300 text-rose-800 text-xs flex items-start gap-1.5">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {saveSuccessMessage && (
        <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs flex items-center gap-1.5">
          <span>✅</span>
          <span>{saveSuccessMessage}</span>
        </div>
      )}

      {/* 0. STATE: Active Countdown (3... 2... 1...) */}
      {countdown !== null ? (
        <div className="p-3.5 bg-gradient-to-br from-amber-50 via-orange-50/80 to-amber-100/90 border-2 border-amber-500 rounded-xl flex flex-col items-center justify-center text-center gap-2 animate-in fade-in duration-150 shadow-md">
          <div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold">
            🎙️ Préparez-vous à prononcer le mot :
          </div>
          <div className="text-stone-900 font-black text-base sm:text-lg tracking-wide">
            « <span className="text-amber-700">{word}</span> »
          </div>
          <div className="w-14 h-14 rounded-full bg-amber-200/80 border-2 border-amber-500 flex items-center justify-center text-2xl font-black text-amber-900 shadow-md animate-bounce my-0.5">
            {countdown > 0 ? countdown : 'PARLEZ !'}
          </div>
          <p className="text-[11px] text-stone-600 font-medium">
            {countdown > 0 ? "L'enregistrement démarre dans..." : "Parlez maintenant dans le micro !"}
          </p>
          <button
            type="button"
            onClick={cancelCountdown}
            className="mt-0.5 px-3 py-1 bg-white hover:bg-stone-100 text-stone-700 text-xs font-bold rounded-lg border border-stone-300 active:scale-95 cursor-pointer transition-all shadow-2xs"
          >
            Annuler
          </button>
        </div>
      ) : isRecording ? (
        /* 1. STATE: Active Recording */
        <div className="p-3 bg-red-50 border-2 border-red-400 rounded-xl flex flex-col items-center gap-2 animate-pulse shadow-sm">
          <div className="flex items-center gap-2 text-red-700 font-bold text-sm">
            <span className="w-3 h-3 rounded-full bg-red-600 animate-ping"></span>
            <span>Enregistrement en cours : {recordingTime}s</span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/80 border border-red-200 text-[10px] text-red-900 font-semibold">
            <span>🔇</span>
            <span>Musique de fond coupée automatiquement</span>
          </div>
          <p className="text-[11px] text-stone-700 font-medium">
            Prononcez clairement le mot « <strong className="text-stone-900">{word}</strong> » dans votre micro.
          </p>
          <div className="flex gap-2 w-full pt-1">
            <button
              type="button"
              onClick={stopRecording}
              className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-bold text-xs uppercase flex items-center justify-center gap-1 shadow-md cursor-pointer border border-red-700"
            >
              <span>⏹️</span>
              <span>Terminer l'enregistrement</span>
            </button>
            <button
              type="button"
              onClick={resetRecording}
              className="px-3 py-2 bg-white hover:bg-stone-100 text-stone-700 rounded-lg font-bold text-xs uppercase border border-stone-300 cursor-pointer shadow-2xs"
              title="Annuler sans sauvegarder"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : audioDataUrl ? (
        /* 2. STATE: New take just recorded, awaiting review or redo */
        <div className="p-3 bg-amber-50/90 border-2 border-amber-400 rounded-xl space-y-2 shadow-2xs">
          <div className="flex items-center justify-between text-xs font-bold text-amber-950">
            <span>Prise audio prête</span>
            <span className="text-[10px] text-stone-500 font-normal">Écoutez ou recommencez si raté</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={playPreview}
              disabled={isPlayingPreview}
              className="flex-1 py-1.5 bg-white hover:bg-amber-50 text-amber-950 border border-amber-300 rounded-lg font-bold text-xs flex items-center justify-center gap-1 cursor-pointer transition-all shadow-2xs"
            >
              <span>{isPlayingPreview ? '🔊 Lecture...' : '▶️ Écouter la prise'}</span>
            </button>
            <button
              type="button"
              onClick={triggerRecordingWithCountdown}
              className="py-1.5 px-3 bg-orange-100 hover:bg-orange-200 text-orange-900 border border-orange-300 rounded-lg font-bold text-xs flex items-center gap-1 cursor-pointer shadow-2xs"
              title="Recommencer l'enregistrement si vous avez raté la prononciation"
            >
              <span>🔄</span>
              <span>Recommencer</span>
            </button>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSaveTake}
              className="flex-1 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white rounded-lg font-bold text-xs uppercase flex items-center justify-center gap-1 shadow-md cursor-pointer border border-emerald-700"
            >
              <span>💾</span>
              <span>Valider cet Audio</span>
            </button>
            <button
              type="button"
              onClick={resetRecording}
              className="px-2.5 py-2 bg-white hover:bg-stone-100 text-stone-600 border border-stone-300 rounded-lg text-xs cursor-pointer shadow-2xs"
              title="Rejeter cette prise"
            >
              ✕
            </button>
          </div>
        </div>
      ) : existingAudio ? (
        /* 3. STATE: Audio is already recorded and saved */
        <div className="p-2.5 bg-emerald-50/90 border border-emerald-300 rounded-xl flex items-center justify-between gap-2 shadow-2xs">
          <button
            type="button"
            onClick={handlePlayExisting}
            disabled={isPlayingExisting}
            className="flex-1 py-1.5 bg-white hover:bg-emerald-50 text-emerald-950 border border-emerald-300 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-all shadow-2xs"
          >
            <span>{isPlayingExisting ? '🔊' : '▶️'}</span>
            <span>{isPlayingExisting ? 'Lecture en cours...' : 'Écouter votre voix'}</span>
          </button>

          <button
            type="button"
            onClick={triggerRecordingWithCountdown}
            className="py-1.5 px-2.5 bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300 rounded-lg font-bold text-xs flex items-center gap-1 cursor-pointer shadow-2xs"
            title="Recommencer un nouvel enregistrement pour remplacer l'actuel"
          >
            <span>🔄</span>
            <span>Refaire</span>
          </button>

          <button
            type="button"
            onClick={handleDeleteExisting}
            className="py-1.5 px-2 bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300 rounded-lg text-xs cursor-pointer shadow-2xs"
            title="Supprimer définitivement cet enregistrement audio"
          >
            🗑️
          </button>
        </div>
      ) : (
        /* 4. STATE: No audio recorded yet */
        <div className="space-y-2">
          <button
            type="button"
            onClick={triggerRecordingWithCountdown}
            className="w-full py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg border border-red-700 shadow-md flex items-center justify-center gap-2 cursor-pointer active:scale-98 transition-all"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-white animate-ping"></span>
            <span>🎙️ Enregistrer la prononciation</span>
          </button>
          <p className="text-[10px] text-stone-500 text-center">
            Compte à rebours de 2 secondes avant l'enregistrement. Vous pourrez réécouter et recommencer si besoin.
          </p>
        </div>
      )}
    </div>
  );
};
